import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, ArrowRight, Zap, Target, BarChart } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';

const References = () => {
  const caseExamples = [
    {
      id: 1,
      category: 'E-ticaret – Dönüşüm Optimizasyonu',
      problem: 'Trafik olmasına rağmen satış oranı düşüktü.',
      approach: 'Kullanıcı akışı incelendi, kritik çıkış noktaları belirlendi. Landing ve ödeme süreci sadeleştirildi.',
      results: [
        'Dönüşüm oranında belirgin artış',
        'Reklam bütçesinin daha verimli kullanımı',
        'Kullanıcı kaybında azalma'
      ],
      icon: TrendingUp,
      color: 'from-cyan-500 to-teal-500'
    },
    {
      id: 2,
      category: 'SaaS – SEO & İçerik Mimarisi',
      problem: 'Organik trafik düşük, arama motorlarında görünürlük yetersizdi.',
      approach: 'Anahtar kelime stratejisi yeniden kurgulandı. İçerik mimarisi ve teknik SEO optimize edildi.',
      results: [
        'Organik trafikte sürekli artış',
        'Hedef kelimelerde ilk sayfa sıralaması',
        'Kaliteli lead akışı'
      ],
      icon: Target,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 3,
      category: 'Hizmet Sektörü – Google & Meta Ads',
      problem: 'Reklam bütçesi yüksek ama dönüşüm maliyeti çok fazlaydı.',
      approach: 'Hedef kitle segmentasyonu yapıldı. Kampanya yapısı ve kreatifler yeniden tasarlandı.',
      results: [
        'Dönüşüm maliyetinde düşüş',
        'ROAS (reklam getirisi) artışı',
        'Bütçe verimliliği'
      ],
      icon: Zap,
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 4,
      category: 'Kurumsal – UX & Landing Sistemi',
      problem: 'Kullanıcılar siteye geliyordu ama form doldurmadan çıkıyordu.',
      approach: 'Kullanıcı davranışı analiz edildi. Landing sayfası ve form akışı basitleştirildi.',
      results: [
        'Form tamamlama oranında artış',
        'Sayfa yükleme hızında iyileşme',
        'Mobil deneyim optimizasyonu'
      ],
      icon: BarChart,
      color: 'from-emerald-500 to-teal-500'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
              Yaklaşım &
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Sonuç Örnekleri</span>
            </h1>

            <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-8">
              <p className="text-gray-300 leading-relaxed">
                Bazı projeler müşteri gizliliği nedeniyle paylaşılmamaktadır.<br />
                Bu bölümde marka isimleri yerine, ürettiğimiz yaklaşımlar ve elde edilen sonuçlar yer almaktadır.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Examples */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {caseExamples.map((example, index) => (
              <div 
                key={example.id}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-500 hover:transform hover:-translate-y-2"
                style={{
                  animation: 'fade-in-up 0.8s ease-out both',
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Icon */}
                <div className="relative w-16 h-16 mb-6">
                  <div className={`absolute inset-0 bg-gradient-to-br ${example.color} rounded-xl flex items-center justify-center`}>
                    <example.icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Category */}
                <h3 className="text-2xl font-bold text-white mb-6">
                  {example.category}
                </h3>

                {/* Problem */}
                <div className="mb-4">
                  <div className="text-cyan-400 font-semibold text-sm mb-2">Problem:</div>
                  <p className="text-gray-300 leading-relaxed">
                    {example.problem}
                  </p>
                </div>

                {/* Approach */}
                <div className="mb-6">
                  <div className="text-cyan-400 font-semibold text-sm mb-2">Yaklaşım:</div>
                  <p className="text-gray-300 leading-relaxed">
                    {example.approach}
                  </p>
                </div>

                {/* Results */}
                <div className="mb-4">
                  <div className="text-cyan-400 font-semibold text-sm mb-3">Sonuç:</div>
                  <div className="space-y-2">
                    {example.results.map((result, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-gray-300 text-sm">{result}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Privacy Note */}
                <div className="pt-4 border-t border-slate-700/50 mt-6">
                  <p className="text-xs text-gray-500 italic">
                    Proje, müşteri gizliliği nedeniyle isim paylaşılmadan sunulmuştur.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Statement */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-12 text-center">
            <p className="text-2xl md:text-3xl text-gray-300 leading-relaxed mb-6">
              Her proje farklıdır.<br />
              Ama yaklaşımımız aynıdır:
            </p>
            <p className="text-xl text-cyan-400 font-semibold">
              Veriyle başla, kullanıcıyı anla, sonuç üret.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Sizin Projeniz İçin
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Ne Yapabiliriz?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Her projeye özel yaklaşım geliştiriyor, ölçülebilir sonuçlar üretiyoruz.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/iletisim">
              <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg">
                Mini Analiz İste
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/hakkimizda">
              <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 px-8 py-6 text-lg">
                Yaklaşımımızı Gör
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default References;
